import { erpListRoute } from '@/definitions/routeTable';
import { erpDefinitionsLoaded } from '@/definitions/store';
import { ERP_PERMISSIONS } from '@/permissions';
import type { SalesDocumentKind } from './salesDocumentKind';
import { SALES_INVOICE_PERMISSIONS, SALES_ORDER_PERMISSIONS } from './salesDocumentPermissions';

/**
 * One „Neues Angebot / Neuer Auftrag / Neue Rechnung“ command, as plain data
 * (I-2348). A sales document has no create route of its own — it is created
 * from the list's dialog, which picks the customer first — so the command opens
 * the list with the intent `?new=<kind>`, and the list opens its dialog through
 * `useRouteIntent` (`@blpde/connect`), like every other app's „Neu …“.
 */
export interface SalesCreateCommandTarget {
  /** The document kind — the command id is `erp.sales.<kind>.new`. */
  kind: SalesDocumentKind;
  /** i18n key of the command label (ERP table). */
  labelKey: string;
  /** Path of the list inside the ERP; Connect mounts it below `/erp`. */
  path: string;
  /** The same right the list's „hinzufügen“ button asks for. */
  requiredPermissions: string;
}

/**
 * The three document kinds that have a list and a create dialog, with the list
 * path of the served route table (BLP/nx#3257). Kept free of views: the shell
 * imports it eagerly through `@erp/connect/shell`, BEFORE the definitions are
 * loaded — so it is a function, and empty until they are. It reads the reactive
 * `erpDefinitionsLoaded`, so a computed or watcher that calls it re-evaluates
 * when they arrive.
 */
export function salesCreateCommandTargets(): readonly SalesCreateCommandTarget[] {
  if (!erpDefinitionsLoaded.value) return [];
  return [
    {
      kind: 'quote',
      labelKey: 'sales.quote.command.new',
      path: erpListRoute('salesQuotes').path,
      requiredPermissions: ERP_PERMISSIONS.salesQuotes.create,
    },
    {
      kind: 'order',
      labelKey: 'sales.order.command.new',
      path: erpListRoute('salesOrders').path,
      requiredPermissions: SALES_ORDER_PERMISSIONS.create,
    },
    {
      kind: 'invoice',
      labelKey: 'sales.invoice.command.new',
      path: erpListRoute('salesInvoices').path,
      requiredPermissions: SALES_INVOICE_PERMISSIONS.create,
    },
  ];
}
